import useSWR from 'swr';
import dayjs from 'dayjs';

import { supabase } from '../../supabase';

const SERVICE_ID_PAGE_SIZE = 1000;
const STATUS_BUCKETS = ['por_vencer', 'pendiente', 'pagado', 'vencido'];

const VENCIMIENTOS_SELECT = `
  id,
  servicio_id,
  fecha_vencimiento,
  importe,
  pagado,
  fecha_pago,
  observaciones,
  notificar,
  importe_pagado,
  año,
  numero_periodo,
  servicios (
    id,
    tipo_servicio,
    empresa_proveedora,
    titular,
    forma_pago,
    detalle,
    observaciones,
    periodicidad,
    referencia_cuenta,
    cantidad_vencimientos
  )
`;

const sanitizeSearch = (value) =>
  String(value || '')
    .trim()
    .replace(/[,%()]/g, ' ')
    .replace(/\s+/g, ' ');

const throwIfError = (error) => {
  if (error) throw error;
};

const fetchServiceIds = async ({ filtros, includeText = false }) => {
  const ids = [];
  const texto = includeText ? sanitizeSearch(filtros.texto) : '';
  let page = 0;
  let keepLoading = true;

  while (keepLoading) {
    let query = supabase
      .from('servicios')
      .select('id')
      .order('id', { ascending: true });

    if (filtros.titular) {
      query = query.eq('titular', filtros.titular);
    }

    if (filtros.tipo_servicio) {
      query = query.eq('tipo_servicio', filtros.tipo_servicio);
    }

    if (texto) {
      query = query.or(
        `tipo_servicio.ilike.%${texto}%,empresa_proveedora.ilike.%${texto}%,detalle.ilike.%${texto}%,titular.ilike.%${texto}%`
      );
    }

    query = query.range(
      page * SERVICE_ID_PAGE_SIZE,
      (page + 1) * SERVICE_ID_PAGE_SIZE - 1
    );

    const { data, error } = await query;
    throwIfError(error);

    const rows = data || [];
    ids.push(...rows.map((row) => row.id));
    keepLoading = rows.length === SERVICE_ID_PAGE_SIZE;
    page += 1;
  }

  return ids;
};

const resolveServiceContext = async (filtros) => {
  const hasServiceFilters = Boolean(filtros.titular || filtros.tipo_servicio);
  const hasText = Boolean(sanitizeSearch(filtros.texto));

  const [baseServiceIds, textServiceIds] = await Promise.all([
    hasServiceFilters
      ? fetchServiceIds({ filtros, includeText: false })
      : Promise.resolve(null),
    hasText
      ? fetchServiceIds({ filtros, includeText: true })
      : Promise.resolve(null),
  ]);

  return {
    baseServiceIds,
    textServiceIds,
    impossible: hasServiceFilters && baseServiceIds.length === 0,
  };
};

const applyStatusFilter = (query, estado) => {
  const hoy = dayjs().format('YYYY-MM-DD');
  const dentroDeSieteDias = dayjs().add(7, 'day').format('YYYY-MM-DD');

  if (estado === 'pagado') {
    return query.eq('pagado', true);
  }

  if (estado === 'vencido') {
    return query.eq('pagado', false).lt('fecha_vencimiento', hoy);
  }

  if (estado === 'por_vencer') {
    return query
      .eq('pagado', false)
      .gte('fecha_vencimiento', hoy)
      .lte('fecha_vencimiento', dentroDeSieteDias);
  }

  if (estado === 'pendiente') {
    return query
      .eq('pagado', false)
      .gt('fecha_vencimiento', dentroDeSieteDias);
  }

  return query;
};

const applyCommonFilters = ({
  query,
  filtros,
  serviceContext,
  anio = null,
  estado = null,
}) => {
  let next = query;

  if (filtros.fechaDesde) {
    next = next.gte('fecha_vencimiento', filtros.fechaDesde);
  }

  if (filtros.fechaHasta) {
    next = next.lte('fecha_vencimiento', filtros.fechaHasta);
  }

  if (anio !== null && anio !== undefined && anio !== '') {
    next = next.eq('año', Number(anio));
  }

  if (serviceContext.baseServiceIds) {
    next = next.in('servicio_id', serviceContext.baseServiceIds);
  }

  const texto = sanitizeSearch(filtros.texto);
  if (texto) {
    const serviceIds = serviceContext.textServiceIds || [];

    if (serviceIds.length) {
      next = next.or(
        `observaciones.ilike.%${texto}%,servicio_id.in.(${serviceIds.join(',')})`
      );
    } else {
      next = next.ilike('observaciones', `%${texto}%`);
    }
  }

  return applyStatusFilter(next, estado || filtros.estado);
};

const createCountQuery = ({ filtros, serviceContext, anio, estado }) => {
  const base = supabase
    .from('vencimientos')
    .select('id', { count: 'exact', head: true });

  return applyCommonFilters({
    query: base,
    filtros,
    serviceContext,
    anio,
    estado,
  });
};

const fetchVencimientosMeta = async (filtros) => {
  const serviceContext = await resolveServiceContext(filtros);

  if (serviceContext.impossible) {
    return {
      total: 0,
      years: [],
      serviceContext,
    };
  }

  let minQuery = supabase
    .from('vencimientos')
    .select('año', { count: 'exact' })
    .not('año', 'is', null);

  minQuery = applyCommonFilters({
    query: minQuery,
    filtros,
    serviceContext,
  })
    .order('año', { ascending: true })
    .limit(1);

  let maxQuery = supabase
    .from('vencimientos')
    .select('año')
    .not('año', 'is', null);

  maxQuery = applyCommonFilters({
    query: maxQuery,
    filtros,
    serviceContext,
  })
    .order('año', { ascending: false })
    .limit(1);

  const [minResult, maxResult] = await Promise.all([minQuery, maxQuery]);
  throwIfError(minResult.error);
  throwIfError(maxResult.error);

  const total = minResult.count || 0;
  if (!total) {
    return {
      total: 0,
      years: [],
      serviceContext,
    };
  }

  const minYear = Number(minResult.data?.[0]?.año);
  const maxYear = Number(maxResult.data?.[0]?.año);

  if (!Number.isFinite(minYear) || !Number.isFinite(maxYear)) {
    return {
      total,
      years: [],
      serviceContext,
    };
  }

  const candidateYears = [];
  for (let year = maxYear; year >= minYear; year -= 1) {
    candidateYears.push(year);
  }

  const yearCounts = await Promise.all(
    candidateYears.map(async (year) => {
      const { count, error } = await createCountQuery({
        filtros,
        serviceContext,
        anio: year,
      });
      throwIfError(error);
      return { year, total: count || 0 };
    })
  );

  return {
    total,
    years: yearCounts.filter((item) => item.total > 0),
    serviceContext,
  };
};

const fetchBucketCount = async ({ filtros, serviceContext, anio, estado }) => {
  const { count, error } = await createCountQuery({
    filtros,
    serviceContext,
    anio,
    estado,
  });
  throwIfError(error);
  return count || 0;
};

const fetchStatusCounts = async ({ filtros, serviceContext, anio }) => {
  const entries = await Promise.all(
    STATUS_BUCKETS.map(async (estado) => [
      estado,
      await fetchBucketCount({ filtros, serviceContext, anio, estado }),
    ])
  );

  return Object.fromEntries(entries);
};

const fetchBucketRows = async ({
  filtros,
  serviceContext,
  anio,
  estado,
  from,
  to,
}) => {
  let query = supabase
    .from('vencimientos')
    .select(VENCIMIENTOS_SELECT);

  query = applyCommonFilters({
    query,
    filtros,
    serviceContext,
    anio,
    estado,
  })
    .order('fecha_vencimiento', { ascending: true, nullsFirst: false })
    .order('id', { ascending: true })
    .range(from, to);

  const { data, error } = await query;
  throwIfError(error);
  return data || [];
};

const fetchVencimientosPage = async ({
  filtros,
  serviceContext,
  anio,
  page,
  pageSize,
  totalYear,
  statusCounts,
}) => {
  if (!anio || !totalYear || serviceContext.impossible) {
    return { rows: [], totalYear: totalYear || 0 };
  }

  const safePage = Math.max(Number(page) || 1, 1);
  const safePageSize = Math.max(Number(pageSize) || 1, 1);
  const pageStart = (safePage - 1) * safePageSize;
  const pageEnd = pageStart + safePageSize - 1;

  if (filtros.estado) {
    const rows = await fetchBucketRows({
      filtros,
      serviceContext,
      anio,
      estado: filtros.estado,
      from: pageStart,
      to: Math.min(pageEnd, totalYear - 1),
    });

    return { rows, totalYear };
  }

  const counts = STATUS_BUCKETS.map((estado) => ({
    estado,
    count: statusCounts?.[estado] || 0,
  }));

  let bucketOffset = 0;
  const requests = [];

  counts.forEach(({ estado, count }) => {
    const bucketStart = bucketOffset;
    const bucketEnd = bucketOffset + count - 1;
    bucketOffset += count;

    if (!count || pageEnd < bucketStart || pageStart > bucketEnd) {
      return;
    }

    const localFrom = Math.max(pageStart, bucketStart) - bucketStart;
    const localTo = Math.min(pageEnd, bucketEnd) - bucketStart;

    requests.push(
      fetchBucketRows({
        filtros,
        serviceContext,
        anio,
        estado,
        from: localFrom,
        to: localTo,
      })
    );
  });

  const chunks = await Promise.all(requests);
  return {
    rows: chunks.flat(),
    totalYear,
  };
};

export const useVencimientos = ({
  filtros,
  anio,
  page = 1,
  pageSize = 50,
}) => {
  const {
    data: meta,
    error: metaError,
    isLoading: metaLoading,
    mutate: mutateMeta,
  } = useSWR(
    ['vencimientos:meta', filtros],
    () => fetchVencimientosMeta(filtros),
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
    }
  );

  const yearEntry = meta?.years?.find(
    (item) => Number(item.year) === Number(anio)
  );
  const canLoadPage = Boolean(meta && yearEntry);
  const needsStatusCounts = canLoadPage && !filtros.estado;

  const {
    data: statusCounts,
    error: statusCountsError,
    isLoading: statusCountsLoading,
    mutate: mutateStatusCounts,
  } = useSWR(
    needsStatusCounts
      ? ['vencimientos:status-counts', filtros, Number(anio)]
      : null,
    () =>
      fetchStatusCounts({
        filtros,
        serviceContext: meta.serviceContext,
        anio: Number(anio),
      }),
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
    }
  );

  const canLoadRows = canLoadPage && (!needsStatusCounts || Boolean(statusCounts));

  const {
    data: pageData,
    error: pageError,
    isLoading: pageLoading,
    mutate: mutatePage,
  } = useSWR(
    canLoadRows
      ? ['vencimientos:page', filtros, Number(anio), Number(page), Number(pageSize)]
      : null,
    () =>
      fetchVencimientosPage({
        filtros,
        serviceContext: meta.serviceContext,
        anio: Number(anio),
        page,
        pageSize,
        totalYear: yearEntry.total,
        statusCounts,
      }),
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
    }
  );


  const refetch = async () => {
    await Promise.all([mutateMeta(), mutateStatusCounts(), mutatePage()]);
  };

  return {
    vencimientos: pageData?.rows || [],
    total: meta?.total || 0,
    totalAnio: yearEntry?.total || 0,
    years: meta?.years || [],
    isLoading:
      metaLoading ||
      (needsStatusCounts && statusCountsLoading) ||
      (canLoadRows && pageLoading),
    isMetaLoading: metaLoading,
    error: metaError || statusCountsError || pageError,
    refetch,
  };
};
